// Parses the "Add items" textarea in ProjectDetail's Items tab. Accepts three
// shapes so a paste from wherever the content list already lives just works:
// one title per line, a CSV with a `title[,brief]` header row, or a JSON
// array of strings/objects.

export interface ParsedItem {
  title: string;
  brief?: string;
}

function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      fields.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  fields.push(cur);
  return fields;
}

function fromJsonArray(parsed: unknown[]): ParsedItem[] {
  const items: ParsedItem[] = [];
  for (const entry of parsed) {
    if (typeof entry === "string") {
      if (entry.trim()) items.push({ title: entry.trim() });
      continue;
    }
    if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const title = typeof obj.title === "string" ? obj.title.trim() : "";
      if (!title) continue;
      const brief = typeof obj.brief === "string" ? obj.brief : undefined;
      items.push({ title, brief });
    }
  }
  return items;
}

export function parseItemPaste(input: string): ParsedItem[] {
  const trimmed = input.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) return fromJsonArray(parsed);
    } catch {
      // Not valid JSON - fall through to line-based parsing.
    }
  }

  const lines = trimmed
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const header = splitCsvLine(lines[0]).map((c) => c.trim().toLowerCase());
  if (header[0] === "title") {
    const briefIdx = header.indexOf("brief");
    const rows: ParsedItem[] = [];
    for (const line of lines.slice(1)) {
      const cols = splitCsvLine(line);
      const title = (cols[0] ?? "").trim();
      if (!title) continue;
      const brief = briefIdx >= 0 ? (cols[briefIdx] ?? "").trim() : undefined;
      rows.push({ title, brief: brief || undefined });
    }
    return rows;
  }

  return lines.map((title) => ({ title }));
}
